import { Button, Form, Space } from "antd";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { useState, useEffect } from "react";
import {
  deleteTemplates,
  addTemplate,
  getCategories,
  updateTemplates,
} from "../repository/data.repository";
import { Template, Category, NotificationType } from "../repository/data.default";
import { TemplatesForm } from "./TemplatesFormDialog.default";
import DynamicForm from "../controls/DynamicForm";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { retry } from "../utils/http";
import { getProblemMessage } from "../utils/http";
import { InputObject } from "../controls/default";
import { TemplatePreviewInput } from "../repository/data.default";
import InModal from "../controls/InModal";
import TemplateTokenPalette from "./TemplateTokenPalette";
import TemplatePreviewModal from "./TemplatePreviewModal";

export interface TemplatesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: Template;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function TemplatesFormDialog(props: TemplatesFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<TemplatesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInput, setPreviewInput] = useState<TemplatePreviewInput>();

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue({
        ...props.initialValues,
        category: {
          value: props.initialValues?.categoryUid,
          display: props.initialValues?.categoryName,
        },
        notificationType: {
          value: props.initialValues?.notificationType,
          display: props.initialValues?.notificationType,
        },
      });
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onError(error: unknown): void {
    setLoading(false);
    setErrorMessage(getProblemMessage(error, "Something went wrong, try again"));
  }

  function onSubmit(): void {
    const { name, subject, body, category, notificationType } = values;
    setLoading(true);
    setErrorMessage(null);
    if (props.mode === "add") {
      addTemplate({
        name,
        subject,
        body,
        categoryUid: category.value,
        notificationType: notificationType.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "update") {
      updateTemplates({
        uid: props.initialValues?.uid,
        rowVersion: props.initialValues?.rowVersion,
        name,
        subject,
        body,
        categoryUid: category.value,
        notificationType: notificationType.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    } else if (props.mode === "delete") {
      deleteTemplates(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(onError);
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Append a {Tag} token to the Body. DynamicForm's editorbox is form-controlled, so writing the
  // field value flows back into the Quill editor. (Cursor-precise insertion is deferred — DynamicForm
  // doesn't forward refs to its controls.)
  function onInsertToken(token: string): void {
    const current = (form.getFieldValue("body") as string) || "";
    form.setFieldValue("body", `${current}${token}`);
  }

  // Snapshot the current (unsaved) form values and preview the rendered output.
  function onPreview(): void {
    setPreviewInput({
      subject: values?.subject,
      body: values?.body,
      notificationType: values?.notificationType?.value,
      categoryUid: values?.category?.value,
    });
    setPreviewOpen(true);
  }

  // Methods

  function reset(): void {
    setValid(false);
    setErrorMessage(null);
    setLoading(false);
    form.resetFields();
  }

  const categoriesRequest = (search: string) => {
    return retry(() =>
      getCategories({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Category>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const notificationOptions = async (): Promise<InputObject[]> => {
    return Object.values(NotificationType).map((type) => ({
      display: type,
      value: type,
    }));
  };

  return (
    <>
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Template", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <Space>
          {props.mode !== "delete" && (
            <Button disabled={loading} onClick={onPreview}>
              Preview
            </Button>
          )}
          <ModalFooter
            mode={props.mode}
            loading={loading}
            valid={valid}
            onCancel={onCancel}
            onSubmit={onSubmit}
          />
        </Space>
      }
    >
      {props.mode !== "delete" && (
        <>
          <DynamicForm
            className="w-auto"
            form={form}
            schema={{
              groups: [
                {
                  controls: [
                    {
                      type: "textbox",
                      name: "name",
                      label: "Name",
                      rules: [
                        {
                          required: true,
                          message: "Title is required",
                        },
                      ],
                    },
                    {
                      type: "textbox",
                      name: "subject",
                      label: "Subject",
                      rules: [
                        {
                          required: true,
                          message: "Subject is required",
                        },
                      ],
                    },
                    {
                      type: "combobox",
                      name: "category",
                      label: "Category",
                      request: categoriesRequest,
                      rules: [
                        {
                          required: true,
                          message: "Category is required",
                        },
                      ],
                    },
                    {
                      type: "combobox",
                      name: "notificationType",
                      label: "Notification Type",
                      request: notificationOptions,
                      rules: [
                        {
                          required: true,
                          message: "Notification Type is required",
                        },
                      ],
                    },
                    {
                      type: "editorbox",
                      name: "body",
                      label: "Body",
                      rows: 5,
                      rules: [
                        {
                          required: true,
                          message: "Body is required",
                        },
                      ],
                    },
                  ],
                },
              ],
            }}
          />
          <TemplateTokenPalette
            onInsert={onInsertToken}
            scanText={`${values?.subject ?? ""} ${values?.body ?? ""}`}
          />
        </>
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="template"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
      <TemplatePreviewModal
        open={previewOpen}
        input={previewInput}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
}

export default TemplatesFormDialog;
